<script setup lang="ts">
// Built on Bootstrap's own nav/nav-tabs/nav-pills markup and CSS (no JS — this project only imports
// bootstrap's CSS, the active tab is entirely driven by our own Vue state via v-model).
import SancareOcticon from '../../components/SancareOcticon.vue'

export interface SancareTabItem {
  key: string
  label: string
  icon?: string | null
  // Colors the tab's label to flag its content (e.g. a rejected or uncertain evaluation)
  status?: 'danger' | 'warning' | null
  // Small colored dot after the label, flagging something about the tab's content apart from its
  // status (e.g. the users' feedbacks on an evaluation)
  dot?: 'primary' | 'danger' | 'warning' | null
  // Tells what the dot flags, on hover and to screen readers
  dotTooltip?: string | null
}

export interface SancareTabsProps {
  items: SancareTabItem[]
  variant?: 'tabs' | 'pills'
  // Bootstrap grid-based equal-width tabs (row/col), matching the full-width tab bars used across
  // the app rather than content-sized tabs.
  equalWidth?: boolean
  multiple?: boolean
}

const props = withDefaults(defineProps<SancareTabsProps>(), {
  variant: 'tabs',
  equalWidth: false,
  multiple: false,
})

// Single-select mode models a string key, multiple-select mode models an array of keys.
const selectedKey = defineModel<string | string[]>({ required: true })

function isActive(key: string): boolean {
  return props.multiple
    ? (selectedKey.value as string[]).includes(key)
    : selectedKey.value === key
}

function selectItem(key: string): void {
  if (!props.multiple) {
    selectedKey.value = key
    return
  }

  const currentKeys = selectedKey.value as string[]
  selectedKey.value = currentKeys.includes(key)
    ? currentKeys.filter((k) => k !== key)
    : [...currentKeys, key]
}
</script>

<template>
  <ul
    class="nav"
    role="tablist"
    :aria-multiselectable="multiple || undefined"
    :class="[
      variant === 'pills' ? 'nav-pills' : 'nav-tabs',
      { 'row g-0': equalWidth },
    ]"
  >
    <li
      v-for="item in items"
      :key="item.key"
      class="nav-item"
      :class="{ 'col text-center': equalWidth }"
    >
      <span
        class="nav-link"
        role="tab"
        tabindex="0"
        :aria-selected="isActive(item.key)"
        :class="[{ active: isActive(item.key) }, item.status ? `nav-link--${item.status}` : '']"
        @click="selectItem(item.key)"
        @keydown.enter.space.prevent="selectItem(item.key)"
      >
        <sancare-octicon
          v-if="item.icon"
          :name="item.icon"
          :width="14"
          class="flex-shrink-0"
        />
        {{ item.label }}
        <span
          v-if="item.dot"
          v-tooltip="item.dotTooltip || null"
          :role="item.dotTooltip ? 'img' : undefined"
          :aria-label="item.dotTooltip || undefined"
          class="d-inline-block flex-shrink-0 rounded-circle nav-link__dot"
          :class="`nav-link__dot--${item.dot}`"
        />
      </span>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
@use '@sancare/typescript-commons/src/assets/styles/variables' as *;

// Matches SancareCheckboxGroup's selected/hover treatment.
$item-checked-bg: #edf1ff;

.nav-link {
  display: inline-flex;
  align-items: center;
  gap: $space-xs;
  color: inherit;
  cursor: pointer;

  &.active {
    color: inherit;
  }
}

// Bootstrap draws one shared border-bottom on the .nav-tabs container and relies on a
// -1px margin trick to make the active link's own (invisible) bottom border coincide with
// it pixel-for-pixel — any color we give that shared line then wins on that exact row no
// matter what we set on the link itself. Simplest robust fix: drop the shared line and let
// each link own its bottom border instead — active is see-through, inactive stays visible.

.nav-tabs {
  border-bottom: none;

  .nav-link {
    border-bottom-color: $sancare-light-grey;

    &.active {
      border-bottom-color: transparent;
    }
  }
}

.nav-pills {
  // Matches the my-1 spacing between SancareCheckboxGroup items.
  gap: .25rem;

  .nav-link {
    padding: $space-s $space-m;
    background-color: transparent;
    border: 1px solid transparent;
    border-radius: $border-radius-s;
    font-weight: bold;
    color: $sancare-purple;
    fill: currentcolor;

    &:hover {
      background: $item-checked-bg;
    }

    &.active {
      background-color: $item-checked-bg;
      border-color: $sancare-light-blue;
    }
  }
}

// Declared after the variant rules so it also wins over their color (same specificity), the
// .active selectors matching the specificity of .nav-link.active's own color.

.nav-link--danger,
.nav-link--danger.active,
.nav-pills .nav-link--danger {
  color: $sancare-red;
}

.nav-link--warning,
.nav-link--warning.active,
.nav-pills .nav-link--warning {
  color: $sancare-orange;
}

// Sized against the label's 14px icon

.nav-link__dot {
  width: 8px;
  height: 8px;

  &--primary {
    background-color: $sancare-purple;
  }

  &--danger {
    background-color: $sancare-red;
  }

  &--warning {
    background-color: $sancare-orange;
  }
}

// equalWidth (row/col grid): stretch each link to the tallest item's height so they're
// all the same size regardless of label wrapping.

.row .nav-link {
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
}

// Tabs anchor the icon+label line to the bottom so wrapped multi-line labels don't look
// misaligned next to single-line ones. Pills stay vertically centered (rule above).

.nav-tabs.row .nav-link {
  flex-direction: column;
  justify-content: flex-end;
}
</style>
